/**
 * Audio Screening Page
 * Main page for audio screening questions
 * MOBILE RESPONSIVE VERSION
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mic, Award, Clock, Info } from 'lucide-react';
import ScreeningQuestions from '../components/ScreeningQuestions';

/**
 * AudioScreeningPage Component
 * 
 * @returns {JSX.Element} AudioScreeningPage component
 */
const AudioScreeningPage = () => {
  const handleScreeningComplete = () => {
    // Handle completion - could redirect or show success message
    console.log('Screening completed successfully!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3 sm:py-4">
          <div className="flex items-center">
            <Link
              to="/career-opportunities"
              className="flex items-center text-gray-600 hover:text-gray-900 mr-2 sm:mr-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Jobs</span>
              <span className="sm:hidden">Back</span>
            </Link>
            <div className="flex items-center min-w-0">
              <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 mr-2 flex-shrink-0" />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                Audio Screening
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6 sm:py-8 max-w-4xl mx-auto">
        {/* Introduction Section */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mic className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Complete Your Audio Screening
            </h2>
            <p className="text-gray-600 text-sm sm:text-base px-2">
              Stand out to employers by completing our audio screening questionnaire. 
              This helps employers get to know you better and can improve your chances of getting hired.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Stand Out</h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Showcase your personality and communication skills
              </p>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <Clock className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Quick Process</h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Takes only 10-15 minutes to complete
              </p>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Info className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Better Matches</h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Help employers find the right fit for their team
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
              <Info className="w-4 h-4 mr-2" />
              Before You Start
            </h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Find a quiet space with minimal background noise</li>
              <li>• Ensure your device's microphone is working properly</li>
              <li>• Speak clearly and at a moderate pace</li>
              <li>• Take your time to think before answering</li>
              <li>• Be authentic and let your personality shine through</li>
            </ul>
          </div>

          {/* Technical Requirements */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">Technical Requirements</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <h4 className="font-medium text-gray-800 mb-1">Browser Support</h4>
                <p>Chrome, Firefox, Safari, or Edge (latest versions)</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-1">Microphone Access</h4>
                <p>You'll be prompted to allow microphone access</p>
              </div>
            </div>
          </div>
        </div>

        {/* Screening Questions Component */}
        <ScreeningQuestions onComplete={handleScreeningComplete} />

        {/* Tips Section */}
        <div className="mt-6 bg-blue-50 p-4 sm:p-6 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-3">Tips for Success:</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></div>
              <span>Be concise but comprehensive in your answers (aim for 1-2 minutes per question)</span>
            </li>
            <li className="flex items-start">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></div>
              <span>Use specific examples to illustrate your points when possible</span>
            </li>
            <li className="flex items-start">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></div>
              <span>Stay positive and enthusiastic throughout your responses</span>
            </li>
            <li className="flex items-start">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></div>
              <span>Don't worry about being perfect - authenticity is more important</span>
            </li>
          </ul>
        </div>

        {/* Privacy Notice */}
        <div className="mt-4 text-center">
          <p className="text-xs sm:text-sm text-gray-500">
            Your audio responses are securely stored and only shared with employers when you apply to their jobs. 
            You can update or delete your screening at any time from your profile settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AudioScreeningPage;